/**
 * Number formatting utilities for internationalization
 * Handles different numeral systems and locale-specific formatting
 */

// Mapping for different numeral systems
const numeralSystems = {
  // Western Arabic numerals (default)
  default: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
  
  // Ge'ez numerals for Amharic
  am: ['0', '፩', '፪', '፫', '፬', '፭', '፮', '፯', '፰', '፱'],
  
  // Korean numerals - Arabic numerals are widely used and understood in modern Korean
  // ko: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
};

/**
 * Format a number according to the specified language's numeral system
 * @param num - The number to format
 * @param language - The language code
 * @returns Formatted number string
 */
export const formatNumber = (num: number, language: string): string => {
  // Special handling for languages with custom numeral systems
  if (numeralSystems[language as keyof typeof numeralSystems]) {
    return convertToLocalNumerals(num, language);
  }
  
  // For most languages, use standard JavaScript number formatting with locale
  try {
    // Use Intl.NumberFormat for proper locale formatting
    const formatter = new Intl.NumberFormat(getLocaleCode(language));
    return formatter.format(num);
  } catch (error) {
    // Fallback to string conversion if Intl.NumberFormat fails
    return num.toString();
  }
};

/**
 * Convert a number to local numerals for any supported language
 * @param num - The number to convert
 * @param language - The language code
 * @returns String with local numerals
 */
const convertToLocalNumerals = (num: number, language: string): string => {
  const targetSystem = numeralSystems[language as keyof typeof numeralSystems];
  if (!targetSystem) {
    return num.toString();
  }
  
  if (num === 0) return targetSystem[0]; // Zero in the target system
  
  const numStr = Math.abs(num).toString();
  
  let result = '';
  for (const digit of numStr) {
    const digitNum = parseInt(digit);
    result += targetSystem[digitNum];
  }
  
  return num < 0 ? '-' + result : result;
};

/**
 * Convert a number to Ge'ez numerals for Amharic (legacy function for compatibility)
 * @param num - The number to convert
 * @returns String with Ge'ez numerals
 */
export const convertToGeezNumerals = (num: number): string => {
  return convertToLocalNumerals(num, 'am');
};

/**
 * Get the locale code for Intl.NumberFormat
 * @param language - The language code
 * @returns Locale code
 */
const getLocaleCode = (language: string): string => {
  const localeMap: { [key: string]: string } = {
    en: 'en-US',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    pt: 'pt-PT',
    ru: 'ru-RU',
    tl: 'tl-PH',
    it: 'it-IT',
    am: 'am-ET',
    ko: 'ko-KR',
  };
  
  return localeMap[language] || 'en-US';
};

/**
 * Format a count with proper pluralization context
 * Useful for "X members", "X questions", etc.
 * @param count - The count number
 * @param language - The language code
 * @returns Formatted count string
 */
export const formatCount = (count: number, language: string): string => {
  return formatNumber(count, language);
};

/**
 * Convert digits in a string to the specified language's numeral system
 * Used for join codes and other alphanumeric strings
 * @param text - The text containing digits to convert
 * @param language - The language code
 * @returns Text with localized numerals
 */
export const localizeDigitsInText = (text: string, language: string): string => {
  // Skip conversion for most languages (keep Arabic numerals)
  if (!numeralSystems[language as keyof typeof numeralSystems]) {
    return text;
  }
  
  const targetSystem = numeralSystems[language as keyof typeof numeralSystems];
  const defaultSystem = numeralSystems.default;
  
  let result = text;
  
  // Replace each digit with its localized equivalent
  for (let i = 0; i < defaultSystem.length; i++) {
    const arabicDigit = defaultSystem[i];
    const localDigit = targetSystem[i];
    result = result.replace(new RegExp(arabicDigit, 'g'), localDigit);
  }
  
  return result;
};

/**
 * Convert localized numerals back to Arabic numerals (reverse conversion)
 * @param text - Text with localized numerals
 * @param language - The language code
 * @returns Text with Arabic numerals
 */
export const delocalizeDigitsInText = (text: string, language: string): string => {
  // Skip conversion for most languages
  if (!numeralSystems[language as keyof typeof numeralSystems]) {
    return text;
  }
  
  const sourceSystem = numeralSystems[language as keyof typeof numeralSystems];
  const defaultSystem = numeralSystems.default;
  
  let result = text;
  
  // Replace each localized digit with its Arabic equivalent
  for (let i = 0; i < sourceSystem.length; i++) {
    const localDigit = sourceSystem[i];
    const arabicDigit = defaultSystem[i];
    result = result.replace(new RegExp(localDigit, 'g'), arabicDigit);
  }
  
  return result;
};

/**
 * Format a join code for display with localized numerals
 * @param code - The join code (e.g., "WX4B7")
 * @param language - The language code
 * @returns Formatted join code with localized numerals and spacing
 */
export const formatJoinCodeForDisplay = (code: string, language: string = 'en'): string => {
  const cleanCode = code.toUpperCase().replace(/\s/g, '');
  
  // Apply number localization
  const localizedCode = localizeDigitsInText(cleanCode, language);
  
  // Add spacing for readability (XX XXX format)
  if (localizedCode.length === 5) {
    return `${localizedCode.slice(0, 2)} ${localizedCode.slice(2)}`;
  }
  
  return localizedCode;
};

/**
 * Clean and normalize join code input (handles both localized and Arabic numerals)
 * @param input - The input join code
 * @param language - The language code
 * @returns Cleaned join code in universal format (Arabic numerals, uppercase)
 */
export const cleanJoinCodeInput = (input: string, language: string = 'en'): string => {
  // Remove spaces and convert to uppercase
  let cleaned = input.toUpperCase().replace(/\s/g, '');
  
  // Convert localized numerals back to Arabic numerals
  cleaned = delocalizeDigitsInText(cleaned, language);
  
  return cleaned;
};
